import React from "react";
import { fonts } from "../../common/mixins";
import styled from "styled-components";
import { PhoneNumberField } from "../phone-number-field/phone-number-field";
import { buildLoginRedirectUrl } from "../../../utils/Constants";
import { DsTextField, Form, FullWidth, Title } from "../../../common";
import { Mail, Phone } from "../../icons";
import { SubTitle } from "../../../common/typography/SubTitle";
import type { UserData } from "../../../hooks/useUserData";
import { Controller, useFormContext } from "react-hook-form";
import type { ReservationFormValues } from "../../../pageComponents/reservationPage/reservation-page.schema";
import { Link } from "../../../common/typography/Links";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { filterNameInput } from "../../../utils/Utils";
import { DsCheckbox } from "../../../common/inputs/DsCheckbox";
import useMiniSite from "../../../hooks/useMiniSite";
import { BREAKPOINT_RESERVATION_PAGE } from "../../../pageComponents/reservationPage/constants/breakpoints";
import { getUserProfileCompleteness } from "../../../utils/userProfile";

type ContactGridProps = {
  $isLoggedIn?: boolean;
};

const FlexContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex-direction: column;
  padding-top: 16px;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
      padding-top: 12px;
      gap: 4px;
  }
`;

const ContactGrid = styled.div<ContactGridProps>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-top: 16px;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    grid-template-columns: 1fr;
  }
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Text = styled.p`
  ${fonts.normalText()};
  margin: 0;
  padding-bottom: 2px;
`;

const CheckboxWrapper = styled.div`
  padding-top: 16px;
`;

interface ContactInformationProps {
  userData: UserData | null;
  isLoggedIn: boolean;
}

export function ContactInformation({
  userData,
  isLoggedIn,
}: ContactInformationProps) {
  const {
    register,
    trigger,
    clearErrors,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<ReservationFormValues>();

  const r = useReservationResources();
  const { isShopflix, isPublic, isInMinisite, isEmbedded, isPaoBC } =
    useMiniSite();

  const { hasEmail, isProfileComplete } = getUserProfileCompleteness(userData);

  return (
    <div id="contact-info-section">
      <Title>{r.ContactInformation}</Title>

      {isLoggedIn ? (
        isProfileComplete ? (
          <FlexContainer>
            <IconContainer id="email">
              <Mail />
              <Text>{userData?.email}</Text>
            </IconContainer>
            <IconContainer id="phone">
              <Phone />
              <Text>
                {userData.phonenumber?.includes(" ")
                  ? `+${userData.phonenumber}`
                  : userData.phonenumber}
              </Text>
            </IconContainer>
          </FlexContainer>
        ) : (
          <>
            <SubTitle>{r.FillMobileNumber}</SubTitle>
            <Form>
              <ContactGrid $isLoggedIn={isLoggedIn}>
                <FullWidth>
                  <DsTextField
                    label={r.FirstName}
                    autoComplete="given-name"
                    required
                    error={!!errors.contact?.firstName}
                    helperText={errors.contact?.firstName?.message}
                    {...register("contact.firstName", {
                      onChange: (e) => {
                        const filtered = filterNameInput(
                          e?.target?.value ?? "",
                        );
                        setValue("contact.firstName", filtered);
                        if (errors.contact?.firstName) {
                          if (filtered) trigger("contact.firstName");
                          else clearErrors("contact.firstName");
                        }
                      },
                    })}
                  />
                </FullWidth>
                <FullWidth>
                  <DsTextField
                    label={r.LastName}
                    autoComplete="family-name"
                    required
                    error={!!errors.contact?.lastName}
                    helperText={errors.contact?.lastName?.message}
                    {...register("contact.lastName", {
                      onChange: (e) => {
                        const filtered = filterNameInput(
                          e?.target?.value ?? "",
                        );
                        setValue("contact.lastName", filtered);
                        if (errors.contact?.lastName) {
                          if (filtered) trigger("contact.lastName");
                          else clearErrors("contact.lastName");
                        }
                      },
                    })}
                  />
                </FullWidth>
                <FullWidth>
                  {hasEmail ? (
                    <DsTextField
                      name="email"
                      label="Email"
                      type="email"
                      autoComplete="email"
                      value={userData?.email ?? ""}
                      disabled
                    />
                  ) : (
                    <DsTextField
                      label="Email"
                      type="email"
                      autoComplete="email"
                      required
                      error={!!errors.contact?.email}
                      helperText={errors.contact?.email?.message}
                      {...register("contact.email", {
                        onChange: (e) => {
                          if (errors.contact?.email) {
                            if (e.target.value) trigger("contact.email");
                            else clearErrors("contact.email");
                          }
                        },
                        onBlur: (e) => {
                          if (e.target.value) trigger("contact.email");
                        },
                      })}
                    />
                  )}
                </FullWidth>
                <FullWidth>
                  <PhoneNumberField />
                </FullWidth>
              </ContactGrid>
              <CheckboxWrapper>
                <Controller
                  name="saveInfoToAccount"
                  control={control}
                  render={({ field }) => (
                    <DsCheckbox
                      checked={field.value}
                      onChange={field.onChange}
                      label={r.SaveContactInfoCheck}
                      required={false}
                    />
                  )}
                />
              </CheckboxWrapper>
            </Form>
          </>
        )
      ) : (
        <>
          {!isShopflix &&
            !isPublic &&
            !isInMinisite &&
            !isEmbedded &&
            !isPaoBC && (
              <SubTitle>
                {r.GotAnAccount}{" "}
                <Link href={buildLoginRedirectUrl()}>{r.LogIn}</Link>{" "}
                {r.FasterCheckout}
              </SubTitle>
            )}

          <Form>
            <ContactGrid $isLoggedIn={isLoggedIn}>
              <FullWidth>
                <DsTextField
                  label={r.FirstName}
                  autoComplete="given-name"
                  required
                  error={!!errors.contact?.firstName}
                  helperText={errors.contact?.firstName?.message}
                  {...register("contact.firstName", {
                    onChange: (e) => {
                      if (e?.target != null) {
                        e.target.value = filterNameInput(e.target.value);
                      }
                      if (errors.contact?.firstName) {
                        if (e?.target?.value) trigger("contact.firstName");
                        else clearErrors("contact.firstName");
                      }
                    },
                  })}
                />
              </FullWidth>

              <FullWidth>
                <DsTextField
                  label={r.LastName}
                  autoComplete="family-name"
                  required
                  error={!!errors.contact?.lastName}
                  helperText={errors.contact?.lastName?.message}
                  {...register("contact.lastName", {
                    onChange: (e) => {
                      if (e?.target != null) {
                        e.target.value = filterNameInput(e.target.value);
                      }
                      if (errors.contact?.lastName) {
                        if (e?.target?.value) trigger("contact.lastName");
                        else clearErrors("contact.lastName");
                      }
                    },
                  })}
                />
              </FullWidth>

              <FullWidth>
                <DsTextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  error={!!errors.contact?.email}
                  helperText={errors.contact?.email?.message}
                  {...register("contact.email", {
                    onChange: (e) => {
                      if (errors.contact?.email) {
                        if (e.target.value) trigger("contact.email");
                        else clearErrors("contact.email");
                      }
                    },
                    onBlur: (e) => {
                      if (e.target.value) trigger("contact.email");
                    },
                  })}
                />
              </FullWidth>

              <FullWidth>
                <PhoneNumberField />
              </FullWidth>
            </ContactGrid>
          </Form>
        </>
      )}
    </div>
  );
}
